/**
 * @component UniversalChatter
 * @description Universeller Chatter (Odoo-inspiriert) fuer Detail-Seiten.
 *   Eine wiederverwendbare Komponente fuer Kommentare, Notizen, Aktivitaeten
 *   und Logs an beliebigen Entitaeten (kunde, case, bewerber, mitarbeiter,
 *   lead, forecast). Unterstuetzt @mention-Autocomplete, Follower-Verwaltung,
 *   Tab-Filterung und 4 Nachrichtentypen.
 *
 * @prop {string}  entityType  - 'kunde', 'case', 'bewerber', 'mitarbeiter', 'lead', 'forecast'
 * @prop {number}  entityId    - ID des Datensatzes
 * @prop {boolean} [collapsed] - startet eingeklappt (default: false)
 * @prop {string}  [className] - zusaetzliche CSS-Klassen
 *
 * @usage
 *   <UniversalChatter entityType="kunde" entityId={kundeId} />
 *
 * @since v2.312.0
 * @i18n chatter.*
 * @endpoints GET /api/chatter/:type/:id, POST /api/chatter/:type/:id/{message,note,activity},
 *   PUT /api/chatter/activity/:id/done, DELETE /api/chatter/:id,
 *   GET/POST/DELETE /api/chatter/:type/:id/{followers,follow,unfollow},
 *   GET /api/chatter/users/search?q=
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from './ConfirmDialog';
import {
  MessageSquare, StickyNote, CalendarClock, History, Send,
  Check, X, AtSign, Users, Plus, Trash2, AlertCircle, Clock,
  Phone, Mail, CalendarDays, ListChecks, ArrowRight,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

// ────────────────────────────────────────────────────────────────────
// Helper
// ────────────────────────────────────────────────────────────────────

const ACTIVITY_ICONS = {
  call: Phone,
  email: Mail,
  meeting: CalendarDays,
  task: ListChecks,
  followup: ArrowRight,
};

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

function timeAgo(iso, t) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return t('chatter.time.now', 'gerade eben');
  if (min < 60) return t('chatter.time.minutesAgo', '{n} min').replace('{n}', min);
  const h = Math.floor(min / 60);
  if (h < 24) return t('chatter.time.hoursAgo', '{n} h').replace('{n}', h);
  const days = Math.floor(h / 24);
  if (days < 7) return t('chatter.time.daysAgo', '{n} d').replace('{n}', days);
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function dueState(dueDate) {
  if (!dueDate) return 'none';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dueDate + 'T00:00:00');
  if (d < today) return 'overdue';
  if (d.getTime() === today.getTime()) return 'today';
  return 'future';
}

// Render @mentions als blaue Links und behalte Whitespace
function renderBody(body, mentions) {
  if (!body) return null;
  const mentionNames = new Set((mentions || []).map(m => (m.user_name || '').toLowerCase()));
  // Split safely on spaces, render @-tokens highlighted
  const tokens = body.split(/(\s+)/);
  return tokens.map((tok, i) => {
    if (tok.startsWith('@')) {
      const name = tok.slice(1).toLowerCase();
      const isMention = [...mentionNames].some(mn => mn.startsWith(name) || name.startsWith(mn.split(' ')[0]));
      if (isMention || tok.length > 1) {
        return (
          <span key={i} className="text-[#338edc] font-medium">{tok}</span>
        );
      }
    }
    return <span key={i}>{tok}</span>;
  });
}

// ────────────────────────────────────────────────────────────────────
// Avatar
// ────────────────────────────────────────────────────────────────────

function Avatar({ name, size = 32 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #338edc, #6366F1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: '#fff',
        fontSize: Math.max(10, Math.round(size * 0.38)),
        fontWeight: 700,
      }}
    >
      {initials(name)}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Mention Autocomplete
// ────────────────────────────────────────────────────────────────────

function MentionDropdown({ users, activeIdx, onPick }) {
  if (!users.length) return null;
  return (
    <div
      className="absolute z-50 left-0 right-0 mb-1 max-h-60 overflow-y-auto rounded-lg border border-line bg-surface-elevated shadow-sm"
      style={{ bottom: '100%' }}
    >
      {users.map((u, i) => (
        <button
          key={u.id}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); onPick(u); }}
          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
            i === activeIdx ? 'bg-[var(--bg-inset,#F8F9FC)]' : 'hover:bg-[var(--bg-inset,#F8F9FC)]'
          }`}
        >
          <Avatar name={u.name} size={24} />
          <div className="flex-1 min-w-0">
            <div className="text-content truncate">{u.name}</div>
            {u.rolle && <div className="text-[10px] text-[var(--text-tertiary)] truncate">{u.rolle}</div>}
          </div>
        </button>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Aktivitaet Modal
// ────────────────────────────────────────────────────────────────────

function ActivityModal({ open, onClose, onSave, t }) {
  const [activityType, setActivityType] = useState('task');
  const [summary, setSummary] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (!open) return;
    setActivityType('task');
    setSummary('');
    setDueDate('');
    setAssignedTo('');
    fetch(`${API}/api/chatter/users/search?q= `, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setUsers(d.users || []))
      .catch(() => {});
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-surface-elevated rounded-card border border-line w-full max-w-md p-5"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-content flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-[#338edc]" />
            {t('chatter.activity.title', 'Aktivität planen')}
          </h3>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-content">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
              {t('chatter.activity.type', 'Typ')}
            </label>
            <select
              value={activityType}
              onChange={e => setActivityType(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
            >
              <option value="call">{t('chatter.activity.types.call', 'Anruf')}</option>
              <option value="email">{t('chatter.activity.types.email', 'E-Mail')}</option>
              <option value="meeting">{t('chatter.activity.types.meeting', 'Meeting')}</option>
              <option value="task">{t('chatter.activity.types.task', 'Aufgabe')}</option>
              <option value="followup">{t('chatter.activity.types.followup', 'Followup')}</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
              {t('chatter.activity.summary', 'Zusammenfassung')}
            </label>
            <input
              type="text"
              value={summary}
              onChange={e => setSummary(e.target.value)}
              placeholder={t('chatter.activity.summaryPh', 'Was ist zu tun?')}
              className="w-full px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                {t('chatter.activity.dueDate', 'Fällig am')}
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                className="w-full px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                {t('chatter.activity.assignedTo', 'Zuweisen an')}
              </label>
              <select
                value={assignedTo}
                onChange={e => setAssignedTo(e.target.value)}
                className="w-full px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
              >
                <option value="">—</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg border border-line text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]"
          >
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button
            onClick={() => onSave({ activity_type: activityType, activity_summary: summary, activity_due_date: dueDate || null, activity_assigned_to: assignedTo ? Number(assignedTo) : null })}
            disabled={!summary && activityType !== 'followup'}
            className="px-3 py-1.5 text-sm rounded-lg bg-[#338edc] text-white hover:bg-[#2a78b8] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('chatter.activity.create', 'Erstellen')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Follower Bar
// ────────────────────────────────────────────────────────────────────

function FollowerBar({ followers, onAdd, onRemove, currentUserId, t }) {
  const [showPicker, setShowPicker] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [users, setUsers] = useState([]);
  const isFollowing = followers.some(f => f.user_id === currentUserId);

  useEffect(() => {
    if (!showPicker) return;
    const ctl = new AbortController();
    const t = setTimeout(() => {
      fetch(`${API}/api/chatter/users/search?q=${encodeURIComponent(searchQ || ' ')}`, { credentials: 'include', signal: ctl.signal })
        .then(r => r.json())
        .then(d => setUsers(d.users || []))
        .catch(() => {});
    }, 200);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [showPicker, searchQ]);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Users className="w-3.5 h-3.5 text-[var(--text-tertiary)]" />
      <span className="text-[11px] uppercase tracking-wider text-[var(--text-tertiary)] font-medium">
        {t('chatter.followers.label', 'Follower')} ({followers.length})
      </span>
      {followers.map(f => (
        <button
          key={f.id}
          onClick={() => onRemove(f.user_id)}
          title={`${f.name} — ${t('chatter.followers.unfollow', 'entfernen')}`}
          className="group flex items-center gap-1.5 px-1.5 py-1 rounded-full border border-line bg-[var(--bg-inset,#F8F9FC)] hover:border-red-500/50 transition-colors"
        >
          <Avatar name={f.name} size={18} />
          <span className="text-[11px] text-content max-w-[80px] truncate">{f.name}</span>
        </button>
      ))}
      <div className="relative">
        <button
          onClick={() => setShowPicker(s => !s)}
          className="flex items-center gap-1 px-2 py-1 rounded-full border border-dashed border-line text-[11px] text-[var(--text-tertiary)] hover:border-[#338edc] hover:text-[#338edc] transition-colors"
        >
          <Plus className="w-3 h-3" />
          {t('chatter.followers.add', 'Hinzufügen')}
        </button>
        {showPicker && (
          <div className="absolute z-50 top-full mt-1 right-0 w-64 rounded-lg border border-line bg-surface-elevated shadow-sm p-2">
            <input
              type="text"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder={t('chatter.followers.search', 'Suchen...')}
              className="w-full px-2 py-1.5 text-xs rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-1 focus:ring-primary-500/30"
              autoFocus
            />
            <div className="mt-2 max-h-48 overflow-y-auto">
              {users.filter(u => !followers.some(f => f.user_id === u.id)).map(u => (
                <button
                  key={u.id}
                  onClick={() => { onAdd(u.id); setShowPicker(false); setSearchQ(''); }}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-xs hover:bg-[var(--bg-inset,#F8F9FC)]"
                >
                  <Avatar name={u.name} size={20} />
                  <span className="text-content truncate">{u.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {!isFollowing && currentUserId && (
        <button
          onClick={() => onAdd(currentUserId)}
          className="ml-auto px-2 py-1 rounded-full border border-[#338edc] text-[11px] text-[#338edc] hover:bg-[#338edc] hover:text-white transition-colors"
        >
          {t('chatter.followers.followMe', 'Folgen')}
        </button>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Message Item
// ────────────────────────────────────────────────────────────────────

function MessageItem({ msg, onActivityToggle, onDelete, currentUserId, t }) {
  const isOwn = msg.author_id === currentUserId;
  const isNote = msg.message_type === 'note';
  const isActivity = msg.message_type === 'activity';
  const isLog = msg.message_type === 'log';

  const borderColor = isNote
    ? 'border-l-amber-400'
    : isActivity
    ? 'border-l-[#338edc]'
    : isLog
    ? 'border-l-[var(--text-muted)]'
    : 'border-l-transparent';

  const bgColor = isNote
    ? 'bg-amber-50/40 dark:bg-amber-900/10'
    : isActivity
    ? 'bg-blue-50/30 dark:bg-blue-900/10'
    : isLog
    ? 'bg-[var(--bg-inset,#F8F9FC)] opacity-80'
    : '';

  if (isLog) {
    return (
      <div className={`flex items-start gap-2 px-3 py-2 border-l-2 ${borderColor} ${bgColor} text-xs`}>
        <History className="w-3.5 h-3.5 text-[var(--text-muted)] mt-0.5 flex-shrink-0" />
        <div className="flex-1 text-[var(--text-tertiary)]">
          <span className="font-medium text-[var(--text-secondary)]">{msg.author_name}</span>
          {' '}
          {msg.body || (
            <>
              <span className="font-medium">{msg.log_field}</span>
              {': '}
              <span className="line-through">{msg.log_old_value || '—'}</span>
              {' → '}
              <span className="text-[#338edc]">{msg.log_new_value || '—'}</span>
            </>
          )}
        </div>
        <span className="text-[10px] text-[var(--text-muted)] flex-shrink-0">{timeAgo(msg.created_at, t)}</span>
      </div>
    );
  }

  return (
    <div className={`flex items-start gap-3 px-3 py-3 border-l-2 ${borderColor} ${bgColor} group`}>
      <Avatar name={msg.author_display_name || msg.author_name} size={32} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-content">{msg.author_display_name || msg.author_name}</span>
          {isNote && (
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 font-medium">
              {t('chatter.note.badge', 'Interne Notiz')}
            </span>
          )}
          {isActivity && (
            <ActivityBadge msg={msg} t={t} />
          )}
          <span className="text-[11px] text-[var(--text-tertiary)] ml-auto">{timeAgo(msg.created_at, t)}</span>
          {isOwn && (
            <button
              onClick={() => onDelete(msg.id)}
              className="opacity-0 group-hover:opacity-100 text-[var(--text-tertiary)] hover:text-red-500 transition-opacity"
              title={t('common.delete', 'Löschen')}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>

        {isActivity && (
          <ActivityCard msg={msg} onToggle={onActivityToggle} t={t} />
        )}

        {msg.body && (
          <div className={`mt-1 text-sm text-content whitespace-pre-wrap break-words ${isActivity ? 'mt-2' : ''}`}>
            {renderBody(msg.body, msg.mentions)}
          </div>
        )}

        {Array.isArray(msg.mentions) && msg.mentions.some(m => m.task_created && m.task_id) && (
          <div className="mt-2 space-y-1">
            {msg.mentions.filter(m => m.task_created && m.task_id).map(m => {
              const dueStr = m.task_due_date
                ? new Date(m.task_due_date).toLocaleDateString('de-AT')
                : t('chatter.mention.noDue', 'kein Datum');
              const done = m.task_status === 'erledigt';
              return (
                <a
                  key={m.id}
                  href="/meine-aufgaben"
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium transition-colors ${done
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-[#338edc] hover:bg-blue-100'}`}
                >
                  <ListChecks className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">
                    {t('chatter.mention.taskFor', 'Task')} #{m.task_id} {t('chatter.mention.for', 'für')} {m.user_name}
                    {m.task_title ? ` — ${m.task_title}` : ''}
                  </span>
                  <span className="ml-auto shrink-0 opacity-80">
                    {done ? t('chatter.activity.done', 'Erledigt') : `${t('chatter.mention.due', 'Fällig')}: ${dueStr}`}
                  </span>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityBadge({ msg, t }) {
  const state = dueState(msg.activity_due_date);
  const cls =
    state === 'overdue' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
    : state === 'today' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
    : state === 'future' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)]';
  const label =
    msg.activity_done ? t('chatter.activity.done', 'Erledigt')
    : state === 'overdue' ? t('chatter.activity.overdue', 'Überfällig')
    : state === 'today' ? t('chatter.activity.today', 'Heute')
    : state === 'future' ? t('chatter.activity.future', 'Zukunft')
    : t('chatter.activity.label', 'Aktivität');
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${msg.activity_done ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : cls}`}>
      {label}
    </span>
  );
}

function ActivityCard({ msg, onToggle, t }) {
  const Icon = ACTIVITY_ICONS[msg.activity_type] || ListChecks;
  const dateStr = msg.activity_due_date
    ? new Date(msg.activity_due_date + 'T00:00:00').toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit' })
    : null;
  return (
    <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-surface-elevated border border-line">
      <Icon className="w-4 h-4 text-[#338edc] mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium text-content ${msg.activity_done ? 'line-through opacity-60' : ''}`}>
          {msg.activity_summary || t('chatter.activity.types.' + msg.activity_type, msg.activity_type)}
        </div>
        <div className="text-[11px] text-[var(--text-tertiary)] mt-0.5 flex items-center gap-3 flex-wrap">
          {dateStr && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {dateStr}
            </span>
          )}
          {msg.assigned_display_name && (
            <span className="flex items-center gap-1">
              <AtSign className="w-3 h-3" />
              {msg.assigned_display_name}
            </span>
          )}
        </div>
      </div>
      <button
        onClick={() => onToggle(msg.id, !msg.activity_done)}
        className={`flex-shrink-0 px-2 py-1 rounded text-[11px] font-medium border transition-colors ${
          msg.activity_done
            ? 'border-emerald-500/50 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
            : 'border-[#338edc] text-[#338edc] hover:bg-[#338edc] hover:text-white'
        }`}
      >
        <Check className="w-3 h-3 inline -mt-0.5 mr-0.5" />
        {msg.activity_done ? t('chatter.activity.reopen', 'Reaktivieren') : t('chatter.activity.markDone', 'Erledigt')}
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────────────────────────

export default function UniversalChatter({ entityType, entityId, collapsed = false, className = '' }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const currentUserId = user?.id;

  const [messages, setMessages] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeText, setComposeText] = useState('');
  const [sending, setSending] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Mention autocomplete state
  const [mentionUsers, setMentionUsers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionIdx, setMentionIdx] = useState(0);
  const textareaRef = useRef(null);
  const mentionDebounceRef = useRef(null);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => { if (mentionDebounceRef.current) clearTimeout(mentionDebounceRef.current); };
  }, []);

  // ─── Load timeline ───
  const load = useCallback(async () => {
    if (!entityType || !entityId) return;
    try {
      const [msgRes, folRes] = await Promise.all([
        fetch(`${API}/api/chatter/${entityType}/${entityId}?filter=all`, { credentials: 'include' }),
        fetch(`${API}/api/chatter/${entityType}/${entityId}/followers`, { credentials: 'include' }),
      ]);
      if (msgRes.ok) {
        const d = await msgRes.json();
        setMessages(d.messages || []);
      }
      if (folRes.ok) {
        const d = await folRes.json();
        setFollowers(d.followers || []);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  // ─── Mention autocomplete ───
  const handleTextChange = useCallback((e) => {
    const val = e.target.value;
    setComposeText(val);
    const cursor = e.target.selectionStart || val.length;
    const before = val.slice(0, cursor);
    // Match @<optional text> — also triggers on bare @ with no chars after it
    const match = before.match(/(?:^|[\s])@([A-Za-zÄÖÜäöüß][A-Za-zÄÖÜäöüß ._-]*)?$/) || before.match(/^@([A-Za-zÄÖÜäöüß][A-Za-zÄÖÜäöüß ._-]*)?$/);
    if (match) {
      const q = match[1] || '';
      setMentionQuery(q);
      if (mentionDebounceRef.current) clearTimeout(mentionDebounceRef.current);
      mentionDebounceRef.current = setTimeout(() => {
        fetch(`${API}/api/chatter/users/search?q=${encodeURIComponent(q)}`, { credentials: 'include' })
          .then(r => r.json())
          .then(d => { setMentionUsers(d.users || []); setMentionIdx(0); })
          .catch(() => {});
      }, 200);
    } else {
      setMentionQuery(null);
      setMentionUsers([]);
      if (mentionDebounceRef.current) clearTimeout(mentionDebounceRef.current);
    }
  }, []);

  const pickMention = useCallback((u) => {
    if (!textareaRef.current) return;
    const ta = textareaRef.current;
    const cursor = ta.selectionStart || composeText.length;
    const before = composeText.slice(0, cursor);
    const after = composeText.slice(cursor);
    const newBefore = before.replace(/@([A-Za-zÄÖÜäöüß][A-Za-zÄÖÜäöüß ._-]*)?$/, `@${u.name} `);
    const newText = newBefore + after;
    setComposeText(newText);
    setMentionQuery(null);
    setMentionUsers([]);
    setTimeout(() => {
      ta.focus();
      const pos = newBefore.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  }, [composeText]);

  const handleKeyDown = useCallback((e) => {
    if (mentionQuery !== null && mentionUsers.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => (i + 1) % mentionUsers.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx(i => (i - 1 + mentionUsers.length) % mentionUsers.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(mentionUsers[mentionIdx]); return; }
      if (e.key === 'Escape') { setMentionQuery(null); setMentionUsers([]); return; }
    }
  }, [mentionQuery, mentionUsers, mentionIdx, pickMention]);

  // ─── Send message ───
  const handleSend = useCallback(async () => {
    const body = composeText.trim();
    if (!body) return;
    const kind = 'message';
    setSending(true);
    // Optimistic UI
    const tempId = -Date.now();
    const optimistic = {
      id: tempId,
      message_type: kind,
      body,
      author_id: currentUserId,
      author_name: user?.vorname && user?.nachname ? `${user.vorname} ${user.nachname}` : (user?.username || 'Du'),
      author_display_name: user?.vorname && user?.nachname ? `${user.vorname} ${user.nachname}` : (user?.username || 'Du'),
      created_at: new Date().toISOString(),
      mentions: [],
    };
    setMessages(prev => [optimistic, ...prev]);
    setComposeText('');
    setMentionQuery(null);
    setMentionUsers([]);
    try {
      const res = await fetch(`${API}/api/chatter/${entityType}/${entityId}/${kind}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw new Error('send failed');
      await load();
    } catch {
      // rollback
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setComposeText(body);
    } finally {
      setSending(false);
    }
  }, [composeText, entityType, entityId, currentUserId, user, load]);

  // ─── Activity toggle done (for existing activities in feed) ───
  const handleActivityToggle = useCallback(async (id, done) => {
    try {
      await fetch(`${API}/api/chatter/activity/${id}/done`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ done }),
      });
      await load();
    } catch {
      // ignore
    }
  }, [load]);

  // ─── Delete message ───
  const handleDelete = useCallback((id) => setConfirmDeleteId(id), []);
  const executeDelete = useCallback(async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    if (!id) return;
    try {
      await fetch(`${API}/api/chatter/${id}`, { method: 'DELETE', credentials: 'include' });
      await load();
    } catch { /* ignore */ }
  }, [confirmDeleteId, load]);

  // ─── Follower add/remove ───
  const handleFollow = useCallback(async (userId) => {
    try {
      await fetch(`${API}/api/chatter/${entityType}/${entityId}/follow`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      await load();
    } catch { /* */ }
  }, [entityType, entityId, load]);

  const handleUnfollow = useCallback(async (userId) => {
    try {
      await fetch(`${API}/api/chatter/${entityType}/${entityId}/unfollow`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      await load();
    } catch { /* */ }
  }, [entityType, entityId, load]);

  if (!entityType || !entityId) return null;

  return (
    <div className={`bg-surface-elevated rounded-card border border-line ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-line">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#338edc]" />
          <span className="text-xs font-semibold text-content">{t('chatter.title', 'Nachrichten')}</span>
          {messages.length > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-[#338edc]/10 text-[#338edc]">{messages.length}</span>
          )}
        </div>
        <button
          onClick={() => setIsCollapsed(c => !c)}
          className="text-[var(--text-tertiary)] hover:text-content text-xs flex-shrink-0"
        >
          {isCollapsed ? t('chatter.expand', 'Aufklappen') : t('chatter.collapse', 'Einklappen')}
        </button>
      </div>

      {!isCollapsed && (
        <>
          {/* Composer */}
          <div className="px-4 py-3 border-b border-line">
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={composeText}
                onChange={handleTextChange}
                onKeyDown={handleKeyDown}
                placeholder={t('chatter.compose.placeholder', 'Nachricht schreiben... Nutze @Name um jemanden zu taggen')}
                rows={2}
                className="w-full px-3 py-2 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 resize-none"
              />
              {mentionQuery !== null && mentionUsers.length > 0 && (
                <MentionDropdown users={mentionUsers} activeIdx={mentionIdx} onPick={pickMention} />
              )}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => handleSend()}
                disabled={!composeText.trim() || sending}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-xs font-medium hover:bg-[#2a78b8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-3 h-3" />
                {t('chatter.compose.send', 'Senden')}
              </button>
              <span className="ml-auto text-[10px] text-[var(--text-muted)] hidden sm:inline">
                {t('chatter.compose.hint', 'Tipp: @Name für Erwähnung')}
              </span>
            </div>
          </div>

          {/* Timeline */}
          <div className="max-h-[500px] overflow-y-auto divide-y divide-line">
            {loading ? (
              <div className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">
                <div className="inline-block animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" />
              </div>
            ) : messages.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">
                <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-40" />
                {t('chatter.empty', 'Noch keine Einträge')}
              </div>
            ) : (
              messages.map(msg => (
                <MessageItem
                  key={msg.id}
                  msg={msg}
                  onActivityToggle={handleActivityToggle}
                  onDelete={handleDelete}
                  currentUserId={currentUserId}
                  t={t}
                />
              ))
            )}
          </div>

          {/* Follower Bar */}
          <div className="px-4 py-3 border-t border-line">
            <FollowerBar
              followers={followers}
              onAdd={handleFollow}
              onRemove={handleUnfollow}
              currentUserId={currentUserId}
              t={t}
            />
          </div>
        </>
      )}

      <ConfirmDialog
        open={!!confirmDeleteId}
        onConfirm={executeDelete}
        onCancel={() => setConfirmDeleteId(null)}
        title={t('chatter.deleteTitle', 'Nachricht loeschen')}
        message={t('chatter.confirmDelete', 'Diese Nachricht loeschen?')}
        confirmLabel={t('chatter.deleteConfirm', 'Loeschen')}
        cancelLabel={t('common.cancel', 'Abbrechen')}
        variant="danger"
      />
    </div>
  );
}
